import { format, isSameDay } from 'date-fns';

/**
 * Date-key helpers for the monthly date-picker flow.
 *
 * The picker only ever cares about *local calendar days and months* — never
 * instants. Historically day comparisons round-tripped `Date` objects (which
 * still carry a time component) through ad-hoc string conversions, so a test
 * whose timestamp sat near midnight could compare unequal to the "same" day in
 * the loaded list. Collapsing every comparison to a stable `yyyy-MM-dd` /
 * `yyyy-MM` string, derived once from local time, removes that whole class of
 * bug. Convert at the boundary; compare on the keys.
 */

/** Local calendar day as `yyyy-MM-dd`. */
export function toDayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Local calendar month as `yyyy-MM`. */
export function toMonthKey(date: Date): string {
  return format(date, 'yyyy-MM');
}

/** True when both dates fall in the same local calendar month. */
export function isSameMonthKey(a: Date, b: Date): boolean {
  return toMonthKey(a) === toMonthKey(b);
}

/**
 * Find the entry in `dates` that falls on the same local day as `day`, ignoring
 * any time component. Entries may arrive as `Date`s or date strings, so each is
 * normalised through `new Date(...)` before comparison. Returns `undefined` when
 * no entry matches.
 */
export function findMatchingDate(
  dates: Array<Date | string>,
  day: Date
): Date | undefined {
  const match = dates.find((d) => isSameDay(new Date(d), day));
  return match === undefined ? undefined : new Date(match);
}
